import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { formatEUR, formatDate } from '../../lib/format';
import { RecordPaymentDialog } from '../../components/collection/RecordPaymentDialog';
import {
  Tabs,
  Tile,
  Modal,
  Empty,
  StatusPill,
  labelCls,
  thCls,
  thNum,
  tdCls,
  tdNum,
  apiMessage,
  cmd,
  type Inv,
} from '../../components/collection/collectionShared';

/**
 * Recouvrement — what the garages owe, since when, and what is already being collected. Click an
 * invoice's row to record its payment; its status then follows by itself.
 */

interface Overview {
  today: string;
  kpis: {
    outstanding: number;
    overdue: number;
    over30: number;
    inCollection: number;
    dso: number;
    blocked: number;
    disputes: number;
    toDun: number;
    toRemit: number;
    buckets: Array<{ key: string; label: string; amount: number; count: number }>;
  };
  invoices: Inv[];
}
type Tab = 'retard' | 'echoir' | 'encours' | 'litige' | 'reglee' | 'tout';
const late = (i: Inv) =>
  i.s.late > 0 && i.s.remaining > 0 && i.s.status !== 'LITIGE' && i.s.status !== 'EN_COURS';
const FILTER: Record<Tab, (i: Inv) => boolean> = {
  retard: late,
  echoir: (i) => i.s.status === 'A_ECHOIR' || i.s.status === 'PARTIELLE',
  encours: (i) => i.s.status === 'EN_COURS',
  litige: (i) => i.s.status === 'LITIGE',
  reglee: (i) => i.s.status === 'REGLEE',
  tout: () => true,
};

export default function CollectionOverview() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [sendingLink, setSendingLink] = useState<string | null>(null);
  const sendPayLink = async (inv: Inv) => {
    setSendingLink(inv.id);
    try {
      const { data } = await apiClient.post<{ sentTo: string; amount: number }>(
        `/collection/invoices/${inv.id}/pay-link`,
        {},
        cmd()
      );
      push({
        tone: 'success',
        message: t('collection.overview.cardLinkSent', {
          number: inv.number,
          to: data.sentTo,
          amount: formatEUR(data.amount),
        }),
      });
    } catch (e) {
      showApiError(e);
    } finally {
      setSendingLink(null);
    }
  };
  const [data, setData] = useState<Overview | null>(null);
  const [tab, setTab] = useState<Tab>('retard');
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [mode, setMode] = useState('');
  const [pay, setPay] = useState<{ customerId?: string; invoiceId?: string } | null>(null);
  const [dispute, setDispute] = useState<{ inv: Inv; close: boolean } | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<Overview>('/collection/overview')
      .then((r) => setData(r.data))
      .catch((e) => {
        setData(null);
        showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.invoices ?? [])
      .filter(FILTER[tab])
      .filter((i) => (!type || i.customer?.type === type) && (!mode || i.customer?.mode === mode))
      .filter(
        (i) =>
          !needle ||
          `${i.customer?.name} ${i.customer?.code ?? ''} ${i.number}`.toLowerCase().includes(needle)
      )
      .sort((a, b) => b.s.late - a.s.late || a.dueDate.localeCompare(b.dueDate));
  }, [data, tab, q, type, mode]);
  const count = (k: Tab) => (data?.invoices ?? []).filter(FILTER[k]).length;
  const k = data?.kpis;

  const nextStep = (i: Inv) => {
    const n = i.nextStep;
    if (!n) return '';
    if (!n.key) return t('collection.overview.allSent');
    return n.later ? t('collection.overview.stepAt', { label: n.label, day: n.day }) : n.label;
  };

  return (
    <div>
      <PageHeader
        title={t('collection.overview.title')}
        description={t('collection.overview.description')}
        actions={
          <Button size="sm" onClick={() => setPay({})}>
            <Plus className="h-4 w-4" /> {t('collection.pay.title')}
          </Button>
        }
      />
      {k && (
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label={t('collection.overview.kpiDue')}
            value={formatEUR(k.outstanding)}
            sub={t('collection.overview.kpiDueSub', { amount: formatEUR(k.inCollection) })}
          />
          <Tile
            label={t('collection.overview.kpiOverdue')}
            value={formatEUR(k.overdue)}
            alert={k.overdue > 0}
            sub={t('collection.overview.kpiOverdueSub', { amount: formatEUR(k.over30) })}
            onClick={() => setTab('retard')}
          />
          <Tile
            label={t('collection.overview.kpiBlocked')}
            value={k.blocked}
            alert={k.blocked > 0}
            sub={t('collection.overview.kpiBlockedSub', { count: k.toDun })}
          />
        </div>
      )}
      {k && (
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          {k.buckets.map((b) => (
            <span
              key={b.key}
              className="rounded-md border bg-card px-2.5 py-1 text-muted-foreground"
            >
              {b.label} <b className="ml-1 tabular-nums text-foreground">{formatEUR(b.amount)}</b>{' '}
              <span className="ml-0.5">({b.count})</span>
            </span>
          ))}
          <span className="rounded-md border bg-card px-2.5 py-1 text-muted-foreground">
            DSO{' '}
            <b className="ml-1 tabular-nums text-foreground">
              {t('collection.common.days', { n: k.dso })}
            </b>
          </span>
        </div>
      )}
      <div className="rounded-lg border bg-card shadow-sm">
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            {
              key: 'retard',
              label: t('collection.overview.tab.retard'),
              count: count('retard'),
              alert: true,
            },
            { key: 'echoir', label: t('collection.overview.tab.echoir'), count: count('echoir') },
            {
              key: 'encours',
              label: t('collection.overview.tab.encours'),
              count: count('encours'),
            },
            { key: 'litige', label: t('collection.overview.tab.litige'), count: count('litige') },
            { key: 'reglee', label: t('collection.overview.tab.reglee'), count: count('reglee') },
            { key: 'tout', label: t('collection.overview.tab.tout') },
          ]}
        />
        <div className="grid grid-cols-1 gap-2 border-b p-3 sm:grid-cols-3">
          <label className="block">
            <span className={labelCls}>{t('collection.overview.search')}</span>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('collection.overview.searchHint')}
            />
          </label>
          <label className="block">
            <span className={labelCls}>{t('collection.overview.type')}</span>
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">{t('collection.common.all')}</option>
              <option value="B2B_ACCOUNT">{t('collection.type.B2B_ACCOUNT')}</option>
              <option value="B2B_PREPAID">{t('collection.type.B2B_PREPAID')}</option>
            </Select>
          </label>
          <label className="block">
            <span className={labelCls}>{t('collection.overview.mode')}</span>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="">{t('collection.common.all')}</option>
              {(['TRANSFER', 'LCR', 'SEPA', 'CHECK'] as const).map((m) => (
                <option key={m} value={m}>
                  {t(`collection.mode.${m}`)}
                </option>
              ))}
            </Select>
          </label>
        </div>
        {data === null ? null : rows.length === 0 ? (
          <Empty>{t('collection.overview.empty')}</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className={thCls}>{t('collection.col.customer')}</th>
                  <th className={thCls}>{t('collection.col.invoice')}</th>
                  <th className={thNum}>{t('collection.col.remaining')}</th>
                  <th className={thCls}>{t('collection.col.status')}</th>
                  <th className={thCls} />
                  <th className={thCls}>{t('collection.col.nextStep')}</th>
                  <th className={thNum}>{t('collection.col.amount')}</th>
                  <th className={thCls}>{t('collection.col.mode')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => {
                  const open = i.s.remaining > 0.004;
                  return (
                    <tr
                      key={i.id}
                      className={
                        open
                          ? 'cursor-pointer border-b last:border-b-0 hover:bg-muted/50'
                          : 'border-b last:border-b-0'
                      }
                      onClick={
                        open
                          ? () => setPay({ customerId: i.customer?.id, invoiceId: i.id })
                          : undefined
                      }
                      title={
                        open ? t('collection.overview.rowHint', { number: i.number }) : undefined
                      }
                    >
                      <td className={tdCls}>
                        <Link
                          to={`/collection/accounts/${i.customer?.id}`}
                          className="font-semibold hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {i.customer?.name}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {i.customer?.code ? `${i.customer.code} · ` : ''}
                          {t(`collection.type.${i.customer?.type}`)}
                        </div>
                      </td>
                      <td className={tdCls}>
                        <span className="font-mono text-xs">{i.number}</span>
                        <div className="text-xs text-muted-foreground">
                          {t('collection.overview.dueOn', { date: formatDate(i.dueDate) })}
                          {i.kind === 'RECAP'
                            ? ` · ${t('collection.overview.bls', { count: i.deliveryNotes })}`
                            : ''}
                        </div>
                      </td>
                      <td className={tdNum}>
                        <b>{formatEUR(i.s.remaining)}</b>
                        {i.s.pending > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {t('collection.overview.pendingAmt', {
                              amount: formatEUR(i.s.pending),
                            })}
                          </div>
                        )}
                      </td>
                      <td className={tdCls}>
                        <StatusPill s={i.s} />
                      </td>
                      <td className={tdCls} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1 whitespace-nowrap">
                          {open && (
                            <Button
                              size="sm"
                              onClick={() =>
                                setPay({ customerId: i.customer?.id, invoiceId: i.id })
                              }
                            >
                              {t('collection.overview.collect')}
                            </Button>
                          )}
                          {/* 2026-10-21 — the customer settles by card from its own portal; staff
                              only send the link (they never type a card). */}
                          {open && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={sendingLink === i.id}
                              onClick={() => void sendPayLink(i)}
                            >
                              {t('collection.overview.cardLink')}
                            </Button>
                          )}
                          {open && i.s.status !== 'LITIGE' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDispute({ inv: i, close: false })}
                            >
                              {t('collection.overview.dispute')}
                            </Button>
                          )}
                          {i.s.status === 'LITIGE' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDispute({ inv: i, close: true })}
                            >
                              {t('collection.overview.closeDispute')}
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className={tdCls}>{nextStep(i)}</td>
                      <td className={tdNum}>{formatEUR(i.total)}</td>
                      <td className={tdCls}>
                        {i.customer?.mode ? t(`collection.mode.${i.customer.mode}`) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pay && (
        <RecordPaymentDialog
          customerId={pay.customerId}
          invoiceId={pay.invoiceId}
          onClose={() => setPay(null)}
          onDone={load}
        />
      )}
      {dispute && (
        <DisputeDialog
          inv={dispute.inv}
          close={dispute.close}
          onClose={() => setDispute(null)}
          onDone={() => {
            push({
              tone: 'success',
              message: t(
                dispute.close ? 'collection.dispute.closed' : 'collection.dispute.opened',
                { number: dispute.inv.number }
              ),
            });
            setDispute(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/** Open a dispute (reason ≥ 10 characters — the invoice leaves dunning and blocking), or close it. */
export function DisputeDialog({
  inv,
  close,
  onClose,
  onDone,
}: {
  inv: Inv;
  close: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      if (close)
        await apiClient.post(
          `/collection/invoices/${inv.id}/dispute/close`,
          { outcome: text },
          cmd()
        );
      else await apiClient.post(`/collection/invoices/${inv.id}/dispute`, { reason: text }, cmd());
      onDone();
    } catch (e) {
      setErr(apiMessage(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t(close ? 'collection.dispute.closeTitle' : 'collection.dispute.title', {
        number: inv.number,
      })}
      lede={close ? inv.dispute?.reason : t('collection.dispute.lede')}
      onClose={onClose}
      footer={
        <>
          <p className="mr-auto flex-1 text-sm font-semibold text-destructive" role="alert">
            {err}
          </p>
          <Button variant="outline" onClick={onClose}>
            {t('collection.common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (!close && text.trim().length < 10) || !text.trim()}
          >
            {t(close ? 'collection.dispute.closeSave' : 'collection.dispute.save')}
          </Button>
        </>
      }
    >
      <label className="block">
        <span className={labelCls}>
          {t(close ? 'collection.dispute.outcome' : 'collection.dispute.reason')}
        </span>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        {!close && (
          <span className="mt-1 block text-xs text-muted-foreground">
            {t('collection.common.min10')}
          </span>
        )}
      </label>
    </Modal>
  );
}
